import { z } from 'zod'

import { ModifierGroupSchema } from './modifier'

/**
 * Item visibility, as the UI talks about it.
 *
 * - `publish` — shown to guests on the public menu/site.
 * - `draft` — saved but hidden.
 * - `trash` — soft-deleted; can be restored.
 * - `other` — anything WP returns that we don't expose in the UI
 *   (e.g. legacy `pending`, `private`, `auto-draft`, `inherit`).
 *
 * The whole feature is built around the publish/draft toggle. `trash`
 * is shown but not editable from the UI, `other` is rendered as a
 * neutral pill so the list never breaks on unexpected data.
 */
export const DishStatusSchema = z
  .union([z.literal('publish'), z.literal('draft'), z.literal('trash'), z.string()])
  .transform((s): 'publish' | 'draft' | 'trash' | 'other' => {
    if (s === 'publish' || s === 'draft' || s === 'trash') return s
    return 'other'
  })

export const DishSchema = z.object({
  id: z.number().int().positive(),
  title: z.string(),
  /** HTML-stripped excerpt of the long description. */
  description: z.string(),
  status: DishStatusSchema,
  /** Price kept as a string — the server returns it formatted (e.g. "172.00"). */
  price: z.string(),
  categoryIds: z.array(z.number().int().nonnegative()),
  tagIds: z.array(z.number().int().nonnegative()),
  featuredMediaId: z.number().int().nonnegative().nullable(),
  /** Pre-resolved image URL if WP gave us one. */
  featuredImageUrl: z.string().url().nullable(),
  /** Inline modifier groups (Carbon Fields `crb_modifier_group`). */
  modifierGroups: z.array(ModifierGroupSchema),
  /**
   * Term ids of shared modifier groups (`ezmodgroup`) attached to
   * this item. Rendered below the inline `modifierGroups` on the
   * public site.
   */
  sharedGroupIds: z.array(z.number().int().nonnegative()),
  /** WP `menu_order` — manual sort key (lower = earlier). */
  menuOrder: z.number().int(),
  modifiedAt: z.string(), // ISO-8601 from `modified_gmt`
})

export type Dish = z.infer<typeof DishSchema>
export type DishStatus = Dish['status']
